import * as _ from 'lodash-es';
import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import PageLayout from '../app/layout/PageLayout';
import TwoColumnRow from '../app/layout/TwoColumnRow';
import EmptyState from '../components/EmptyState';
import InfiniteScrollWaypoint from '../components/InfiniteScrollWaypoint';
import PageTitle from '../components/PageTitle';
import PostSortMenuButton from '../components/PostSortMenuButton';
import { gql } from '../graphql/generated/gql';
import { PostFilter, PostSort } from '../graphql/generated/graphql';
import allDraftsIcon from '../icons/non-prefilled/16x16/allDrafts.svg';
import draftsIcon from '../icons/non-prefilled/16x16/drafts.svg';
import teamIcon from '../icons/non-prefilled/16x16/team.svg';
import CreatePostButton from '../post/CreatePostButton';
import PostRow from '../post/PostRow';
import PostRowMeta from '../post/PostRowMeta';
import PostSectionHeader from '../post/PostSectionHeader';
import PostSectionTitleFilterMenu from '../post/PostSectionTitleFilterMenu';
import PageMeta from '../utils/PageMeta';
import ConnectionHelper from '../utils/graphql/ConnectionHelper';
import * as styles from './DraftsView.module.scss';

const POST_SORT_OPTIONS = [
  PostSort.Viewed,
  PostSort.Edited,
  PostSort.Created,
  PostSort.Alphabetical,
] satisfies PostSort[];

const FILTER_OPTIONS = [
  {
    label: 'My Drafts',
    icon: draftsIcon,
    filters: [PostFilter.Draft, PostFilter.Owner],
  },
  {
    label: 'Shared Drafts',
    icon: teamIcon,
    filters: [PostFilter.Draft, PostFilter.Shared],
  },
  {
    label: 'All Drafts',
    icon: allDraftsIcon,
    filters: [PostFilter.Draft],
  },
] as const;

const DEFAULT_ORDER = PostSort.Viewed;

const METADATA_ATTRIBUTE = {
  [PostSort.Viewed]: 'viewedAt',
  [PostSort.Edited]: 'editedAt',
  [PostSort.Created]: 'insertedAt',
  [PostSort.Alphabetical]: 'editedAt',
} as const;

const DRAFTS_VIEW_QUERY = gql(/** @complexity 8 */ `
  query DraftsView(
    $afterCursor: String
    $filters: [PostFilter!]
    $sortBy: PostSort
  ) {
    listPosts(
      first: 100,
      after: $afterCursor
      filters: $filters
      sortBy: $sortBy
    ) {
      edges {
        node {
          id
          editedAt
          insertedAt
          ...PostRow_post
        }
        viewedAt
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

interface UsePostListQueryProps {
  shouldIncludeArchived: boolean;
  filterOption: (typeof FILTER_OPTIONS)[number];
  selectedFilterOption: (typeof FILTER_OPTIONS)[number];
  shouldPrefetch: boolean;
  sortBy: PostSort;
}

const usePostListQuery = ({
  shouldIncludeArchived,
  filterOption,
  selectedFilterOption,
  shouldPrefetch,
  sortBy,
}: UsePostListQueryProps) => {
  const result = useQuery(DRAFTS_VIEW_QUERY, {
    variables: {
      filters: shouldIncludeArchived
        ? [...filterOption.filters]
        : [...filterOption.filters, PostFilter.Unarchived],
      sortBy,
    },
    skip: selectedFilterOption.label !== filterOption.label && !shouldPrefetch,
  });

  const fetchMore = ConnectionHelper.generateFetchMore(
    DRAFTS_VIEW_QUERY,
    result,
    'listPosts',
  );

  const edges = _.compact(
    result.data?.listPosts?.edges?.map(
      (edge) =>
        edge?.node && {
          node: edge.node,
          viewedAt: edge.viewedAt,
        },
    ),
  );

  return { edges, fetchMore, loading: result.loading };
};

const DraftsView = () => {
  const [sortBy, setSortBy] =
    useState<(typeof POST_SORT_OPTIONS)[number]>(DEFAULT_ORDER);

  const [selectedFilterOption, setSelectedFilterOption] = useState<
    (typeof FILTER_OPTIONS)[number]
  >(FILTER_OPTIONS[0]);
  const [shouldIncludeArchived, setShouldIncludeArchived] = useState(false);
  const [shouldPrefetch, setShouldPrefetch] = useState(false);

  const commonOptions = {
    shouldIncludeArchived,
    shouldPrefetch,
    selectedFilterOption,
    sortBy,
  };

  const myDraftResults = usePostListQuery({
    ...commonOptions,
    filterOption: FILTER_OPTIONS[0],
  });

  const sharedDrafts = usePostListQuery({
    ...commonOptions,
    filterOption: FILTER_OPTIONS[1],
  });

  const allDrafts = usePostListQuery({
    ...commonOptions,
    filterOption: FILTER_OPTIONS[2],
  });

  const results = {
    [FILTER_OPTIONS[0].label]: myDraftResults,
    [FILTER_OPTIONS[1].label]: sharedDrafts,
    [FILTER_OPTIONS[2].label]: allDrafts,
  };

  const currentResult = results[selectedFilterOption.label];

  return (
    <PageLayout.Content noPaddingX noPaddingY>
      <PageMeta title="Drafts - Slab" />
      <div className={styles.container}>
        <TwoColumnRow
          main={
            <PageTitle
              title="Drafts"
              subtitle="Drafts are only visible to you and those you share with. Publish to make them discoverable by your teammates."
            />
          }
        />
        <TwoColumnRow
          full={
            <PostSectionHeader
              title={
                <PostSectionTitleFilterMenu
                  onShouldPrefetch={() => setShouldPrefetch(true)}
                  options={FILTER_OPTIONS}
                  selectedOption={selectedFilterOption}
                  onSelectedOptionChange={setSelectedFilterOption}
                  getCount={(option) => results[option.label].edges.length}
                />
              }
              controls={
                <PostSortMenuButton
                  allowedPostSorts={POST_SORT_OPTIONS}
                  sortBy={sortBy}
                  onSortByChange={setSortBy}
                  isArchivedIncluded={shouldIncludeArchived}
                  onIsArchivedIncludedChange={setShouldIncludeArchived}
                />
              }
            />
          }
        />
        <TwoColumnRow
          full={
            <>
              {!currentResult.loading && currentResult.edges.length === 0 ? (
                <EmptyState
                  imageName="posts"
                  title="Nothing yet."
                  message="So, write something!"
                  action={
                    <CreatePostButton size="large" variant="secondary">
                      Start writing
                    </CreatePostButton>
                  }
                />
              ) : (
                <PostRow.Provider>
                  {currentResult.edges.map((edge) => (
                    <PostRow
                      key={edge.node.id}
                      post={edge.node}
                      meta={
                        <PostRowMeta
                          attribute={METADATA_ATTRIBUTE[sortBy]}
                          values={{
                            viewedAt: edge.viewedAt,
                            editedAt: edge.node.editedAt,
                            insertedAt: edge.node.insertedAt,
                          }}
                        />
                      }
                    />
                  ))}
                </PostRow.Provider>
              )}
              <InfiniteScrollWaypoint fetchMore={currentResult.fetchMore} />
            </>
          }
        />
      </div>
    </PageLayout.Content>
  );
};

export default DraftsView;
